import { useDispatch } from "react-redux";
import { UnknownAction } from "redux";
import { SHARED_REDUCER_ACTIONS } from "../store/reducers/shared.reducer";
import { useNavigate } from "react-router-dom";
import { siteRoutes } from "../utils/helpers/enums/routes";
import { ROLES } from "utils/helpers/enums/roles.enum";
import { GENDERS } from "utils/helpers/enums/shared.enum";
import {
  authAccessibleRoutes,
  roleBasedRoutes,
} from "utils/constants/routes.constants";
import placeholderProfilePic from "assets/images/others/host-side-images/dpIcon.png";
import useUtils from "./useUtils";
const useStore = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { getFilePathWithBackendUrl } = useUtils();

  const dispatchAction = (type: string, payload?: any): void => {
    const dispatcher: UnknownAction = { type, payload };
    dispatch(dispatcher);
  };

  const setLoading = (isLoading: boolean): void => {
    dispatchAction(SHARED_REDUCER_ACTIONS.LOADING, isLoading);
  };

  const setToken = (payload: null | string): void => {
    dispatch({ type: SHARED_REDUCER_ACTIONS.SAVE_TOKEN, payload });
    if (payload) localStorage.setItem("token", payload);
  };

  const setRegisteredData = (payload: any): void => {
    dispatchAction(SHARED_REDUCER_ACTIONS.SAVE_REGISTERED_DATA, payload);
    localStorage.setItem("registeredData", JSON.stringify(payload));
  };

  const getUser = () => {
    const registeredUser = localStorage.getItem("registeredData");
    if (registeredUser) {
      return JSON.parse(registeredUser);
    } else {
      return {};
    }
  };

  // Simple function to update registered data
  const updateRegisteredData = (newData: any) => {
    const currentData = getUser();
    const updatedData = { ...currentData, ...newData };
    setRegisteredData(updatedData);
    return updatedData;
  };

  const getDefaultPathByRole = () => {
    const role = getCurrentRole();
    if (role === ROLES.ATTENDEE) {
      return siteRoutes.attendeeHome;
    } else if (role === ROLES.AMBASSADOR) {
      return siteRoutes.ambassadorHome;
    } else if (role === ROLES.HOST) {
      return siteRoutes.home;
    } else if (role === ROLES.RSVP) {
      return siteRoutes.rsvpHome;
    } else {
      return siteRoutes.minorHome;
    }
  };

  const getSwitchRoles = (): string[] => {
    const { user = null } = getUser() || {};
    if (user) {
      return user.roles;
    } else {
      return [];
    }
  };

  const isProfileCompleted = () => {
    const currentRoleProfile = getCurrentRoleProfile();
    let isCompleted = false;
    if (currentRoleProfile) {
      isCompleted = currentRoleProfile?.profileCompleted;
    }

    return isCompleted;
  };

  const getProfileCompletionData = () => {
    const { user = null } = getUser() || {};
    const profile = getCurrentRoleProfile();
    if (user && profile) {
      return { ...user, ...profile };
    } else {
      return null;
    }
  };

  const setUserCompletionData = (
    formData: any,
    setFormData: Function,
    setValue: Function
  ) => {
    const data = getProfileCompletionData();
    if (data) {
      for (let key in formData) {
        if (key !== "profilePicId") {
          if (key === "profilePicUrl") {
            formData[key] = getFilePathWithBackendUrl(
              data[key]?.fileDownloadUri
            );
            formData.profilePicId = data[key]?.id;
            setValue(key, formData[key]);
            setValue("profilePicId", formData.profilePicId);
          } else {
            if (Array.isArray(formData[key])) {
              // formData[key] = Array.isArray(data[key]) ? data[key] : [];
              // setValue(key, formData[key]);
            } else {
              formData[key] = data[key] ?? "";
              setValue(key, formData[key]);
            }
          }
        }
      }

      setFormData({ ...formData });
    }
  };

  const setAmbassadorCompletionData = (
    formData: any,
    setFormData: Function,
    setValue: Function
  ) => {
    const data = getProfileCompletionData();
    if (data) {
      for (let key in formData) {
        if (key !== "profilePicId") {
          if (key === "profilePicUrl") {
            formData[key] = getFilePathWithBackendUrl(
              data[key]?.fileDownloadUri
            );
            formData.profilePicId = data[key]?.id;
            setValue(key, formData[key]);
            setValue("profilePicId", formData.profilePicId);
          } else {
            if (Array.isArray(formData[key])) {
              formData[key] = Array.isArray(data[key]) ? data[key] : [];
              setValue(key, formData[key]);
            } else {
              formData[key] = data[key] ?? "";
              setValue(key, formData[key]);
            }
          }
        }
      }

      setFormData({ ...formData });
    }
  };

  const setProfileModalOpen = (payload: any): void => {
    dispatchAction(SHARED_REDUCER_ACTIONS.SAVE_PROFILE_MODAL_OPEN, payload);
  };

  const updateProfileCompletionStatus = (role: string): void => {
    const currentUserData = getUser();
    if (currentUserData && currentUserData.profiles && currentUserData.profiles[role]) {
      currentUserData.profiles[role].profileCompleted = true;
      setRegisteredData(currentUserData);
    }
  };

  const getToken = (): string | null => {
    return localStorage.getItem("token");
  };

  const setRole = (role: any): void => {
    localStorage.setItem("role", JSON.stringify(role));
  };

  const getRole = (): any => {
    const role = localStorage.getItem("role");
    if (role) {
      return JSON.parse(role);
    } else {
      return null;
    }
  };

  const getCurrentRole = (): ROLES | null => {
    const { user = null } = getUser() || {};
    if (user) {
      return user.currentRole;
    } else {
      return null;
    }
  };

  const getDefaultRole = (): ROLES | null => {
    const { user = null } = getUser() || {};
    if (user) {
      return user.defaultRole;
    } else {
      return null;
    }
  };

  const getCurrentRoleProfile = () => {
    const currentRole = getCurrentRole();
    let profile = null;
    if (currentRole) {
      const { profiles = {} } = getUser() || {};
      if (Object.keys(profiles).length) {
        profile = profiles[currentRole.toLowerCase()];
      }
    }

    return profile;
  };

  const getUpgradeRouteByRole = (role: ROLES) => {
    if (role === ROLES.ATTENDEE) {
      return null;
    } else if (role === ROLES.AMBASSADOR) {
      return siteRoutes.ambassadorRole;
    } else if (role === ROLES.HOST) {
      return siteRoutes.registerInfo;
    } else if (role === ROLES.RSVP) {
      return null;
    } else {
      return null;
    }
  };

  const logout = () => {
    dispatchAction(SHARED_REDUCER_ACTIONS.LOGOUT);
    localStorage.clear();
    navigate(siteRoutes.login);
  };

  const getUserGender = (): GENDERS => {
    const { user = null } = getUser();
    return user?.gender;
  };

  const getUserProfilePic = () => {
    const { user = null } = getUser();
    return user?.profilePicUrl?.fileDownloadUri
      ? getFilePathWithBackendUrl(user?.profilePicUrl?.fileDownloadUri)
      : placeholderProfilePic;
  };

  const getUserProfilePicById = (): number => {
    const { user = null } = getUser();
    return user?.profilePicUrl?.id || 0;
  };

  const getUserFullName = () => {
    const { user = null } = getUser();
    return `${user?.firstName} ${user?.lastName}`;
  };

  const getUserLanguage = () => {
    const { user = null } = getUser();
    return user?.language;
  };

  const getUserEmail = () => {
    const { user = null } = getUser();
    return user?.email;
  };

  const getUserName = () => {
    const { user = null } = getUser();
    return user?.username;
  };

  const getUserPhone = () => {
    const { user = null } = getUser();
    return user?.phone;
  };

  const getUserzipCode = () => {
    const { user = null } = getUser();
    return user?.zipCode;
  };

  const getUserDob = () => {
    const { user = null } = getUser();
    return user?.dob;
  };

  const getUserCurrentRole = () => {
    const { user = null } = getUser();
    return user?.currentRole;
  };

  const getUserFamilyName = () => {
    const { user = null } = getUser();
    return `${user?.lastName}`;
  };

  const getUserEngagementLevel = () => {
    const { profiles = null } = getUser();
    return `${profiles?.ambassador?.engagementLevel}`;
  };

  const getAmbassadorTierResposibilities = () => {
    const { profiles = null } = getUser();
    return profiles?.ambassador?.myTierResposibilities;
  }

  const hasAccess = (route: string): boolean => {
    const currentRole = getCurrentRole();
    if (currentRole) {
      return roleBasedRoutes[currentRole].includes(route);
    } else {
      return false;
    }
  };

  const isAuthAccessibleRoute = (route: siteRoutes): boolean => {
    return authAccessibleRoutes.includes(route);
  };

  const getUserSubscription = () => {
    const { user = null } = getUser();
    return user?.subscription ?? null;
  };

  const getUserCity = (): string => {
    const { user = null } = getUser();
    return user?.city ?? "";
  };

  const getAmbassadorId = (): number => {
    const { user = null, profiles = null } = getUser();
    return profiles?.ambassador?.id;
  };

  const getAmbassadorreferralCode = (): string => {
    const { user = null, profiles = null } = getUser();
    return profiles?.ambassador?.referralCode;
  }

  const getAmbassadorEventStatus = (): string => {
    const { user = null, profiles = null } = getUser();
    return profiles?.ambassador?.eventStatus;
  };

  const getLastAdminEvent = (): any => {
    const { user = null, profiles = null } = getUser();
    return profiles?.ambassador?.lastAdminEvent;
  };

  const getbookedSessions = (): any => {
    const { user = null, profiles = null } = getUser();
    return profiles?.ambassador?.bookedSessions;
  };

  const getAmbassadorprofileType = (): any => {
    const { user = null, profiles = null } = getUser();
    return profiles?.ambassador?.profileType;
  };

  const getAmbassadorCreatedAt = (): any => {
    const { user = null, profiles = null } = getUser();
    return profiles?.ambassador?.createdAt;
  };

  const getAmbassadorApproved = (): any => {
    const { user = null, profiles = null } = getUser();
    return profiles?.ambassador?.isApproved;
  };

  const getUserState = (): string => {
    const { user = null } = getUser();
    return user?.state ?? "";
  };

  const getUserAddress = (): string => {
    const { user = null } = getUser();
    return user?.address ?? "";
  };

  const getBusinessAddress = (): string => {
    const { profiles = null } = getUser();
    return profiles?.host?.businessAddress ?? "";
  };

  const getUserChildrenCount = () => {
    const user = getUser();
    const count = user?.profiles?.attendee?.childCount;
    return count ?? 0;
  };

  const getMissionStatement = () => {
    const user = getUser();
    const mission = user?.profiles?.ambassador?.mission;
    return mission;
  };

  const getUserId = (): number => {
    const user = getUser();
    return user?.user?.id;
  };

  // Get user data for forms (single method approach)
  const getUserData = () => {
    const { user = null } = getUser();
    if (!user) return null;
    
    return {
      firstName: user.firstName || "",
      lastName: user.lastName || "",
      email: user.email || "",
      username: user.username || "",
      phone: user.phone || "",
      address: user.address || "",
      city: user.city || "",
      state: user.state || "",
      zipCode: user.zipCode || "",
      language: user.language || "",
      dob: user.dob || "",
      gender: user.gender || "",
      profilePicUrl: user.profilePicUrl?.fileDownloadUri 
        ? getFilePathWithBackendUrl(user.profilePicUrl.fileDownloadUri)
        : placeholderProfilePic,
      profilePicId: user.profilePicUrl?.id || 0,
      currentRole: user.currentRole || null,
      defaultRole: user.defaultRole || null,
    };
  };

  const getTotalEvents = (): number => {
    const user = getUser();
    return user?.profiles?.attendee?.totalEvents;
  };

  const getHostTotalEvents = (): number => {
    const user = getUser();
    return user?.profiles?.host?.totalEvents ?? 0;
  };

  const getAmbassadorEmail = (): string => {
    const { user = null } = getUser();
    return user?.email;
  };
  const getAmbassadorTier = (): number => {
    const user = getUser();
    return user?.profiles?.ambassador?.tier;
  };

    const getAmbassadorLevel = (): string => {
    const user = getUser();
    return user?.profiles?.ambassador?.tier;
  };

  const getAmbassadorTotalEarnedPoints = (): number => {
    const user = getUser();
    return user?.profiles?.ambassador?.totalEarnedPoints;
  };

  const getAmbassadorWallet = (): number => {
    const user = getUser();
    return user?.profiles?.ambassador?.wallet;
  };

  const getAmbassadorTotalSpentPoints = (): number => {
    const user = getUser();
    return user?.profiles?.ambassador?.totalSpentPoints;
  };

  const getTotalMembers = (): number => {
    const user = getUser();
    return user?.profiles?.attendee?.childCount;
  };

  const getPastEvents = (): number => {
    const user = getUser();
    return user?.profiles?.attendee?.pastEvents;
  };

  const getPastEventsCreatedAt = (): number => {
    const user = getUser();
    return user?.profiles?.attendee?.createdAt;
  };

  const setNotificationCount = (count: number): void => {
    dispatchAction(SHARED_REDUCER_ACTIONS.SAVE_NOTIFICATION_COUNT, count);
  };
  return {
    setToken,
    setLoading,
    dispatchAction,
    getUserState,
    logout,
    getToken,
    getUserCity,
    getRole,
    getUserAddress,
    setRole,
    setRegisteredData,
    setProfileModalOpen,
    getUser,
    isProfileCompleted,
    getDefaultPathByRole,
    getProfileCompletionData,
    setUserCompletionData,
    getCurrentRoleProfile,
    getCurrentRole,
    getSwitchRoles,
    getDefaultRole,
    getUpgradeRouteByRole,
    getUserGender,
    getUserProfilePic,
    getUserFullName,
    getUserFamilyName,
    hasAccess,
    isAuthAccessibleRoute,
    getUserSubscription,
    getUserChildrenCount,
    getUserId,
    getBusinessAddress,
    setNotificationCount,
    getTotalEvents,
    getTotalMembers,
    getPastEvents,
    getPastEventsCreatedAt,
    getUserLanguage,
    getUserDob,
    getUserPhone,
    getUserzipCode,
    getUserProfilePicById,
    setAmbassadorCompletionData,
    getUserEngagementLevel,
    getAmbassadorId,
    getMissionStatement,
    getAmbassadorEmail,
    getbookedSessions,
    getAmbassadorprofileType,
    getAmbassadorCreatedAt,
    getAmbassadorApproved,
    getAmbassadorTier,
    getAmbassadorTotalEarnedPoints,
    getAmbassadorWallet,
    getAmbassadorTotalSpentPoints,
    getUserCurrentRole,
    getUserEmail,
    getUserName,
    getAmbassadorTierResposibilities,
    getAmbassadorreferralCode,
    updateProfileCompletionStatus,
    updateRegisteredData,
    getAmbassadorLevel,
    getUserData, // New single method to get user data
    getAmbassadorEventStatus,
    getLastAdminEvent,
    getHostTotalEvents,
  };
};

export default useStore;
